import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Loader2, MessageCircle, RefreshCw, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { Btn, Card, EmptyState, LabeledSelect, PageHeader, Pagination } from "@/components/dashboard/Ui";

const STATUS_STYLES = {
  sent: { icon: CheckCircle2, cls: "bg-[hsl(142_55%_42%)]/15 text-[hsl(142_60%_24%)]" },
  pending: { icon: Clock, cls: "bg-[hsl(38_80%_50%)]/15 text-[hsl(30_70%_28%)]" },
  failed: { icon: XCircle, cls: "bg-destructive/10 text-destructive" },
  cancelled: { icon: XCircle, cls: "bg-secondary text-muted-foreground" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.cancelled;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {status}
    </span>
  );
}

export default function WhatsAppPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [testPhone, setTestPhone] = useState("");

  const { data: cfg } = useQuery({
    queryKey: ["whatsapp-status"],
    queryFn: async () => (await api.get("/whatsapp/status")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp", status, page],
    queryFn: async () =>
      (await api.get("/whatsapp", { params: { status: status || undefined, page, page_size: 25 } })).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["whatsapp"] });
    qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
  };

  const retry = useMutation({
    mutationFn: async (id) => (await api.post(`/whatsapp/${id}/retry`)).data,
    onSuccess: (d) => {
      const s = d.message?.status;
      s === "sent" ? toast.success("Reminder sent") : toast.error(d.message?.error || "Still failing");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const runNow = useMutation({
    mutationFn: async () => (await api.post("/whatsapp/run-now")).data,
    onSuccess: (d) => {
      toast.success(`Queued ${d.queued?.queued ?? 0}, retried ${d.retried?.retried ?? 0}`);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sendTest = useMutation({
    mutationFn: async () => (await api.post("/whatsapp/test", { phone: testPhone })).data,
    onSuccess: (d) => {
      d.message?.status === "sent"
        ? toast.success("Test message sent")
        : toast.error(d.message?.error || "Test failed");
      setTestPhone("");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <PageHeader
        title="WhatsApp reminders"
        description="Clients are sent a WhatsApp reminder automatically, one day before a confirmed appointment."
        actions={
          <Btn variant="secondary" onClick={() => runNow.mutate()} disabled={runNow.isPending} data-testid="whatsapp-run-now">
            {runNow.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} Check now
          </Btn>
        }
      />

      {/* Configuration state */}
      {cfg && !cfg.configured && (
        <Card className="border-[hsl(46_55%_80%)] bg-[hsl(46_90%_94%)] p-5" data-testid="whatsapp-not-configured">
          <p className="flex items-center gap-2 font-bold text-[hsl(30_70%_25%)]">
            <AlertTriangle className="h-5 w-5" /> WhatsApp is not connected yet
          </p>
          <p className="mt-1 text-sm text-[hsl(30_70%_25%)]">
            Reminders are being recorded below but nothing is being sent. Add these to the server
            environment and restart:
          </p>
          <ul className="mt-2 list-disc ps-5 text-sm font-mono text-[hsl(30_70%_25%)]">
            {(cfg.missing || []).map((m) => <li key={m}>{m}</li>)}
          </ul>
          <p className="mt-3 text-sm text-[hsl(30_70%_25%)]">
            You also need a Meta-approved message template named{" "}
            <strong className="font-mono">{cfg.template}</strong> with three body
            parameters: name, date, time.
          </p>
        </Card>
      )}

      {cfg && cfg.configured && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="flex items-center gap-2 font-semibold text-[hsl(142_60%_24%)]">
              <CheckCircle2 className="h-4 w-4" /> Connected
            </span>
            <span className="text-muted-foreground">
              Template <strong className="font-mono text-foreground">{cfg.template}</strong>
            </span>
            <span className="text-muted-foreground">
              Sent <strong className="text-foreground">{cfg.reminder_hours}h</strong> before the appointment
            </span>
            {!cfg.enabled && (
              <span className="font-semibold text-destructive">Reminders are switched off</span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
            <div>
              <label htmlFor="wa-test" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Send a test message
              </label>
              <input
                id="wa-test"
                className="emerge-input !h-10 w-56 text-sm"
                dir="ltr"
                placeholder="+9613123456"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                data-testid="whatsapp-test-phone"
              />
            </div>
            <Btn onClick={() => sendTest.mutate()} disabled={!testPhone || sendTest.isPending} data-testid="whatsapp-test-send">
              {sendTest.isPending ? <Loader2 className="animate-spin" /> : <Send />} Send test
            </Btn>
          </div>
        </Card>
      )}

      {cfg?.preview && (
        <Card className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message the client receives</p>
          <p className="mt-2 max-w-xl rounded-[12px] rounded-bl-sm bg-[hsl(142_45%_92%)] px-4 py-3 text-sm text-foreground">
            {cfg.preview}
          </p>
        </Card>
      )}

      {/* Log */}
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <LabeledSelect
            id="wa-status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="min-w-[170px]"
            aria-label="Filter by status"
            data-testid="whatsapp-filter-status"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </LabeledSelect>
          {cfg?.counts && (
            <span className="text-sm text-muted-foreground">
              {cfg.counts.sent || 0} sent · {cfg.counts.pending || 0} pending · {cfg.counts.failed || 0} failed
            </span>
          )}
          {isLoading && <Loader2 className="ms-auto h-4 w-4 animate-spin text-primary" />}
        </div>

        {items.length === 0 && !isLoading ? (
          <EmptyState
            icon={MessageCircle}
            title="No reminders yet"
            description="A reminder appears here once a confirmed appointment comes within a day."
            testId="whatsapp-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm" data-testid="whatsapp-table">
              <thead className="bg-secondary/70 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">Queued</th>
                  <th className="px-4 py-3 text-start font-semibold">Client</th>
                  <th className="px-4 py-3 text-start font-semibold">Phone</th>
                  <th className="px-4 py-3 text-start font-semibold">Appointment</th>
                  <th className="px-4 py-3 text-start font-semibold">Status</th>
                  <th className="px-4 py-3 text-end font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-t border-border table-row-hover" data-testid="whatsapp-row">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtRelative(m.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{m.beneficiary_name || m.contact_name}</p>
                      {m.kind === "test" && <p className="text-xs text-muted-foreground">test message</p>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3" dir="ltr">{m.to}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {m.appointment_start ? fmtDateTime(m.appointment_start) : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={m.status} />
                      {m.error && <p className="mt-1 max-w-xs truncate text-xs text-destructive" title={m.error}>{m.error}</p>}
                    </td>
                    <td className="px-4 py-3 text-end">
                      {["failed", "pending"].includes(m.status) && (
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => retry.mutate(m.id)}
                          disabled={retry.isPending || !cfg?.configured}
                          title={cfg?.configured ? "Send again" : "Connect WhatsApp first"}
                          data-testid="whatsapp-retry"
                        >
                          <RefreshCw /> Retry
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.total > 0 && (
          <div className="border-t border-border p-4">
            <Pagination page={page} pageSize={data.page_size} total={data.total} onChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
